import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Course, Section } from '@/types';
import { courseApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EnquiryPanel } from '@/components/public/EnquiryPanel';
import { BrandedExternalLink } from '@/components/public/BrandedExternalLink';
import { DetailPageSkeleton } from '@/components/public/DetailPageSkeleton';
import { PriceDisplay } from '@/components/public/PriceDisplay';
import { getTutorAvatarUrl } from '@/utils/tutorAvatar';

const formatDuration = (seconds: number) => {
  if (!seconds) return 'Self paced';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
};

export const CourseDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [course, setCourse] = useState<Course | null>(null);
  const [requestStatus, setRequestStatus] = useState<'loading' | 'success' | 'not-found'>('loading');
  const [requestSlug, setRequestSlug] = useState<string | null>(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) {
      setCourse(null);
      setRequestSlug(null);
      setRequestStatus('not-found');
      return;
    }

    const controller = new AbortController();

    const fetchCourse = async () => {
      setRequestSlug(slug);
      setRequestStatus('loading');
      setCourse(null);
      setExpandedSectionIndex(null);
      try {
        const data = await courseApi.getCourseBySlug(slug, controller.signal);
        if (data) {
          setCourse(data);
          setExpandedSectionIndex(data.sections.length ? 0 : null);
          setRequestStatus('success');
        } else {
          setCourse(null);
          setExpandedSectionIndex(null);
          setRequestStatus('not-found');
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch course:', error);
          setCourse(null);
          setRequestStatus('not-found');
        }
      }
    };

    void fetchCourse();
    return () => controller.abort();
  }, [slug]);

  if (slug && (requestSlug !== slug || requestStatus === 'loading')) {
    return <DetailPageSkeleton />;
  }

  if (requestStatus === 'not-found' || !course) {
    return (
      <section className="bg-app">
        <div className="page-shell">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Course not found</h1>
        </div>
      </section>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isTutor = user?.role === 'tutor';
  const canEditAsTutor = Boolean(isTutor && course.createdById === user?.id);
  const originalCourseFee =
    course.price != null && course.price > 0 ? `INR ${course.price.toLocaleString('en-IN')}` : undefined;
  const courseFeeDisplay = course.isFree ? (
    <PriceDisplay isFree originalPrice={originalCourseFee} variant="detail" />
  ) : (
    originalCourseFee || 'Enquire for fee details'
  );
  const tutorLinks = course.tutor
    ? [course.tutor.linkedinUrl, course.tutor.websiteUrl].filter(
        (link, index, collection): link is string => Boolean(link?.trim()) && collection.indexOf(link) === index
      )
    : [];

  return (
    <section className="min-h-full bg-app">
      <div className="page-shell space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-slate-950 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
          <img src={course.thumbnailUrl} alt={course.title} className="h-72 w-full object-cover sm:h-80 lg:h-[26rem]" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/42 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 lg:p-10">
            <div className="flex flex-wrap gap-2">
              {[course.level, course.teachingMode || 'Online', ...(course.audience || []).slice(0, 2)].map((item) => (
                <span key={item} className="rounded-full border border-white/16 bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                  {item}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {course.title}
            </h1>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-8">
            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                Course Overview
              </p>
              <div className="mt-5 divide-y divide-slate-200">
                <div className="pb-5">
                  <p className="whitespace-pre-line text-base leading-7 text-slate-700">{course.description}</p>
                </div>
                <div className="py-5">
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Pre-requisite</p>
                  <p className="mt-2 whitespace-pre-line text-base leading-7 text-slate-700">
                    {course.prerequisite || 'No prior experience required. Our team can guide you based on your goals.'}
                  </p>
                </div>
                <div className="grid gap-5 py-5 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Level</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{course.level}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Audience</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">
                      {course.audience?.length ? course.audience.join(', ') : 'Open to all learners'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Course Hours</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{formatDuration(course.totalDuration)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Teaching Mode</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{course.teachingMode || 'Online'}</p>
                  </div>
                </div>
              </div>
            </Card>

            {course.tutor ? (
              <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                  Tutor Profile
                </p>
                <div className="mt-4 flex flex-col gap-5 sm:flex-row">
                  <img
                    src={getTutorAvatarUrl(course.tutor)}
                    alt={course.tutor.name}
                    className="h-20 w-20 rounded-[1.5rem] border border-slate-200 bg-slate-100 object-cover"
                    loading="lazy"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-2xl font-semibold text-slate-950">{course.tutor.name}</h2>
                      {typeof course.tutor.yearsExperience === 'number' && course.tutor.yearsExperience > 0 ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                          {course.tutor.yearsExperience}+ years experience
                        </span>
                      ) : null}
                    </div>
                    {course.tutor.title ? <p className="mt-1 text-sm text-slate-600">{course.tutor.title}</p> : null}
                    {course.tutor.bio ? <p className="mt-4 text-sm leading-7 text-slate-600">{course.tutor.bio}</p> : null}

                    {course.tutor.expertise?.length ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {course.tutor.expertise.map((tag) => (
                          <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-700">
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {tutorLinks.length ? (
                      <div className="mt-4 flex flex-wrap items-center gap-1">
                        {tutorLinks.map((link) => (
                          <BrandedExternalLink key={link} href={link} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Card>
            ) : null}

            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                    Course Content
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {course.sections.length} section{course.sections.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {course.sections.map((section: Section, sectionIndex) => {
                  const isOpen = expandedSectionIndex === sectionIndex;
                  return (
                    <div key={section.id || `${course.id}-section-${sectionIndex}`} className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50/80">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedSectionIndex((current) => (current === sectionIndex ? null : sectionIndex))
                        }
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-white"
                      >
                        <div>
                          <p className="text-lg font-semibold text-slate-950">{section.title}</p>
                          <p className="mt-1 text-sm text-slate-600">
                            {section.lessons.length} lesson{section.lessons.length === 1 ? '' : 's'}
                            {section.duration ? ` - ${formatDuration(section.duration)}` : ''}
                          </p>
                        </div>
                        {isOpen ? (
                          <ChevronUp className="h-5 w-5 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-500" />
                        )}
                      </button>

                      {isOpen ? (
                        <div className="border-t border-slate-200 bg-white px-5 py-5">
                          {section.description ? (
                            <p className="mb-4 text-sm leading-7 text-slate-600">{section.description}</p>
                          ) : null}

                          {section.lessons.length ? (
                            <div className="space-y-3">
                              {section.lessons.map((lesson, index) => (
                                <div key={lesson.id} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-4">
                                  <div className="flex items-start gap-3">
                                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--accent-solid))/0.1] text-sm font-semibold text-[rgb(var(--accent-solid))]">
                                      {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                      <p className="font-semibold text-slate-950">{lesson.title}</p>
                                      {lesson.description ? (
                                        <p className="mt-1 text-sm leading-6 text-slate-600">{lesson.description}</p>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                              This section is concise and does not include separate lessons.
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          <div className="xl:pt-2">
            <div className="space-y-5">
              {isAdmin ? (
                <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                    Course Management
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Admin accounts can edit, review, and manage the full public presentation of this course.
                  </p>
                  <div className="mt-5 space-y-3">
                    <Button className="w-full" onClick={() => navigate(`/admin/courses/${course.id}/edit`)}>
                      Edit Course
                    </Button>
                    <Button variant="secondary" className="w-full" onClick={() => navigate('/admin/courses')}>
                      View All Courses
                    </Button>
                  </div>
                </Card>
              ) : canEditAsTutor ? (
                <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                    Tutor Management
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    This course belongs to your tutor account, so you can update it directly from the dashboard.
                  </p>
                  <div className="mt-5 space-y-3">
                    <Button className="w-full" onClick={() => navigate(`/tutor/courses/${course.id}/edit`)}>
                      Edit Course
                    </Button>
                    <Button variant="secondary" className="w-full" onClick={() => navigate('/tutor/courses')}>
                      View My Courses
                    </Button>
                  </div>
                </Card>
              ) : isTutor ? (
                <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                    Tutor Access
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Tutor accounts can browse public course pages while admissions guidance remains focused on visitors.
                  </p>
                  <Button variant="secondary" className="mt-5 w-full" onClick={() => navigate('/tutor/courses')}>
                    Back to Tutor Dashboard
                  </Button>
                </Card>
              ) : (
                <EnquiryPanel
                  itemType="course"
                  itemId={course.id}
                  sectionLabel="Course Fee"
                  heading={courseFeeDisplay}
                  headingCaption={course.isFree ? 'Current access' : 'Indicative amount'}
                  headingVariant="featured"
                  ctaLabel="I'm interested"
                  submitLabel="Submit"
                  showNotesField={false}
                  hideFieldLabels
                  revealFieldsOnCta
                  phoneFieldMode="india-mobile"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
